import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { classNames, formattedDate, cleanString } from "../utils";
import Table from "./Table";
import {
  ReportListInputApi,
  ReportSummaryApi,
  ReportSummaryItemApi,
  WORKFLOW_REPORT_STATUS_ENUM,
} from "../api/Reports";
import ReportDetailLink from "./ReportDetailLink";
import ReportNavigationModal from "./ReportNavigationModal";
import { useGetReportInfoList } from "../api/ReportsApi";
import ReportStatus from "./ReportStatus";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import Pagination from "../shared-components/Pagination";
import { useAppContext } from "../AppProviders";
import { CacheKeys } from "../api/Cache";
import { LoadedSince } from "../shared-components/LoadedSince";
import { genericErrorLoadingMessage, reportFailed } from "./utils";
import { ClipboardIcon } from "@heroicons/react/24/outline";
import {
  WORKFLOW_MANUAL_SCRAPE_STATUS,
  WORKFLOW_STEP_STATUS_ENUM,
} from "../api/Workflows";
import { useFamaFlags } from "../api/FamaFlags";
import {
  ReportEntry,
  ReportEntryStyle,
  ReportRowItem,
} from "./helper-components/TableModels";
import { useSyncSelectedReportFromList } from "./useSyncSelectedReportFromList";

function buildAmendmentReportListInput(
  filter: string,
  page: number,
  pageSize: number
): ReportListInputApi {
  return {
    person_name: filter,
    page_size: pageSize,
    page: page - 1,
    workflow_status: [WORKFLOW_REPORT_STATUS_ENUM.AMENDMENT],
    descending: true,
  };
}

const ReportsAmendment = () => {
  const { enableProductReportsForClientTab } = useFamaFlags();
  const {
    isLoading: loadingReports,
    data: reportsData,
    mutate: getReportsFetchList,
    error: reportsError,
  } = useGetReportInfoList(false, enableProductReportsForClientTab);

  const { search } = useLocation();
  const { refreshCache } = useAppContext();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [filter, setSearch] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(1);
  const [selectedReport, setSelectedReport] = useState<ReportSummaryItemApi>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    canManageReports,
    canPostScrape,
    canConfirmProfile,
    canPostRate,
    canNavAmendment,
  } = usePermissionValidator();
  const pageSize = 10;

  const tableHeader = [
    "Name / Company",
    { text: "Status", customClass: "w-44" },
    "Check Type / Behavior Kit",
    "Report ID",
    "Completed Date",
    "Client Reference ID",
    "Opened By",
  ];

  useEffect(() => {
    setSearch(cleanString(searchParams.get("search")?.trim()));

    if (searchParams.get("page")) {
      setPage(Number(searchParams.get("page")));
    }
  }, [searchParams]);

  const refetchAmendmentReports = useCallback(() => {
    refreshCache(CacheKeys.reports);
    getReportsFetchList(buildAmendmentReportListInput(filter, page, pageSize));
  }, [getReportsFetchList, filter, page, pageSize, refreshCache]);

  useEffect(() => {
    getReportsFetchList(buildAmendmentReportListInput(filter, page, pageSize));
  }, [getReportsFetchList, filter, page, pageSize]);

  useSyncSelectedReportFromList(selectedReport, setSelectedReport, reportsData);

  if (reportsError) {
    return (
      <div className="p-4 rounded-xl bg-red-300 text-red-900">
        {genericErrorLoadingMessage}
      </div>
    );
  }

  function handleReportClick(report: ReportSummaryItemApi) {
    if (!canManageReports) return;
    setSelectedReport(report);
    if (report.step_name) toggleOpen(true);
  }

  function toggleOpen(isOpen: boolean) {
    setIsModalOpen(isOpen);
  }

  function clearCache() {
    refetchAmendmentReports();
  }

  function checkStatusAndPermission(
    step_name: string | null,
    step_payload: any
  ) {
    return (
      (step_name === WORKFLOW_STEP_STATUS_ENUM.PostsManualRate &&
        canPostRate) ||
      (step_name === WORKFLOW_STEP_STATUS_ENUM.PostsManualScrape &&
        step_payload.MANUAL_SCRAPE_READY ===
          WORKFLOW_MANUAL_SCRAPE_STATUS.IN_PROGRESS &&
        canPostScrape) ||
      (step_name === WORKFLOW_STEP_STATUS_ENUM.ProfilesManualRate &&
        canConfirmProfile) ||
      (step_name === WORKFLOW_STEP_STATUS_ENUM.WebContentReview &&
        canPostRate) || // TODO: define permission for webcontent
      (step_name === WORKFLOW_STEP_STATUS_ENUM.ArticlesManualEdit &&
        canPostRate) || // TODO: define permission for webcontent
      (step_name === WORKFLOW_STEP_STATUS_ENUM.PostsManualEdit &&
        canNavAmendment) ||
      (step_name === WORKFLOW_STEP_STATUS_ENUM.AmendmentQA &&
        canNavAmendment) ||
      (step_name === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyAmend &&
        canNavAmendment)
    );
  }

  const convertReportsAmendmentToRows = (
    reportsData: ReportSummaryApi | undefined
  ): ReportRowItem<ReportSummaryItemApi>[] => {
    if (!reportsData) return [];
    const reportRows: ReportRowItem<ReportSummaryItemApi>[] =
      reportsData.report_summaries.map((report: ReportSummaryItemApi) => {
        const data: ReportEntry[] = [
          {
            text: report.full_name,
            style: ReportEntryStyle.COMPONENT,
            component: (
              <ReportDetailLink
                report={report}
                onClick={handleReportClick}
                showRerunIndicator={false}
                disabled={
                  !canManageReports ||
                  !checkStatusAndPermission(
                    report.step_name,
                    report.step_payload
                  )
                }
                onUnlockSuccess={refetchAmendmentReports}
              ></ReportDetailLink>
            ),
          },
          {
            text: report.report_status,
            style: ReportEntryStyle.COMPONENT,
            component: (
              <ReportStatus
                stepStatusName={report.step_name}
                failed={reportFailed(report)}
                report={report}
                allowResumeOnClick={true}
              />
            ),
            customClass: "w-44",
          },
          {
            component: (
              <div className="flex flex-col">
                <span className="border-b-2">{report.blueprint_name}</span>{" "}
                <small className="block">
                  {report.behavior_kit_name ?? "General"}
                </small>
              </div>
            ),
            text:
              report.blueprint_name + " " + report.behavior_kit_name ??
              "General",
            style: ReportEntryStyle.COMPONENT,
          },
          {
            text: report.report_uuid,
            style: ReportEntryStyle.COMPONENT,
            component: (
              <div className="flex gap-1">
                <ClipboardIcon
                  onClick={() => {
                    navigator.clipboard.writeText(report.report_uuid);
                  }}
                  className="w-4 cursor-pointer"
                ></ClipboardIcon>
                <span className={classNames("w-16 truncate")}>
                  {report.report_uuid}
                </span>
              </div>
            ),
          },
          {
            text: formattedDate(report.finish_at), //todo: completed date
            style: ReportEntryStyle.TEXT,
          },
          {
            text: `${report.client_report_id}`,
            style: ReportEntryStyle.TEXT,
          },
          {
            text: `${report.locked_by_name}`,
            style: ReportEntryStyle.TEXT,
          },
        ];
        return {
          report: report,
          report_uuid: report.report_uuid,
          isLocked: report.is_locked,
          data: data,
        };
      });
    return reportRows;
  };

  return (
    <>
      <>
        <LoadedSince reportData={reportsData} onClear={clearCache} />
        <Table
          isLoading={loadingReports}
          header={tableHeader}
          data={convertReportsAmendmentToRows(reportsData)}
        />
        {!loadingReports && reportsData && (
          <Pagination
            pageSize={pageSize}
            currentPage={page}
            totalCount={reportsData.total_rows}
            search={filter}
          />
        )}
      </>

      {selectedReport && (
        <ReportNavigationModal
          report={selectedReport}
          isOpen={isModalOpen}
          toggleOpen={toggleOpen}
        />
      )}
    </>
  );
};

export default ReportsAmendment;
